<div class="st-dialog-header">
  <span class="st-dialog-icon" aria-hidden="true">
    <i
      class="bi"
      [class.bi-arrow-left-right]="!bulk && currentManagerId"
      [class.bi-person-plus]="bulk || !currentManagerId"></i>
  </span>
  <div class="st-dialog-heading">
    <h5 class="st-dialog-title">{{ title }}</h5>
    <p class="st-dialog-subtitle">{{ subtitle }}</p>
  </div>
  <button
    type="button"
    class="btn-close"
    aria-label="Close"
    [disabled]="isSaving"
    (click)="activeModal.dismiss()"></button>
</div>

<div class="st-dialog-body">
  @for (message of otherErrors; track $index) {
    <div class="alert alert-danger py-2 px-3 small mb-3" role="alert">{{ message }}</div>
  }

  <!-- Who is moving -->
  @if (bulk) {
    @if (failureSummary) {
      <div class="alert alert-warning py-2 px-3 small mb-2" role="alert">{{ failureSummary }}</div>
    }
    @for (message of listErrors; track $index) {
      <div class="alert alert-danger py-2 px-3 small mb-2" role="alert">{{ message }}</div>
    }

    <ul class="picker-employees list-unstyled">
      @for (employee of employees; track employee.id) {
        <li class="picker-employee" [class.has-error]="employeeErrors[employee.id]">
          <span class="st-avatar is-small" [ngClass]="levelClassOf(employee)" aria-hidden="true">
            {{ initials(employee) }}
          </span>
          <div class="picker-employee-main">
            <span class="picker-employee-name">{{ employee.name }}</span>
            <span class="picker-employee-meta">
              #{{ employee.employee_number }}, reports to {{ currentManagerNames[employee.id] || 'Admin' }}
            </span>
            @for (message of employeeErrors[employee.id] ?? []; track $index) {
              <span class="picker-employee-error">{{ message }}</span>
            }
          </div>
          <button
            type="button"
            class="picker-employee-remove"
            [disabled]="isSaving"
            [attr.aria-label]="'Remove ' + employee.name + ' from the selection'"
            title="Remove from selection"
            (click)="onRemoveEmployee(employee.id)">
            <i class="bi bi-x-lg"></i>
          </button>
        </li>
      } @empty {
        <li class="picker-empty">Nobody selected. Close this dialog and select people in the tree.</li>
      }
    </ul>
  } @else {
    @if (employees[0]; as employee) {
      <div class="st-person-card">
        <span class="st-avatar" [ngClass]="levelClassOf(employee)" aria-hidden="true">{{ initials(employee) }}</span>
        <div class="st-person-main">
          <div class="st-person-name">{{ employee.name }}</div>
          <div class="st-person-meta">
            <span class="badge app-badge" [ngClass]="badgeClassOf(employee)">{{ employee.designation }}</span>
            <span>#{{ employee.employee_number }}</span>
          </div>
          <div class="st-route">
            <span class="st-route-label">Reports to</span>
            <span class="st-route-stop" [class.is-admin]="!currentManagerId">{{ currentManagerName }}</span>
            <i class="bi bi-arrow-right" aria-hidden="true"></i>
            @if (selectedManager && selectedManager.id !== currentManagerId) {
              <span class="st-route-stop is-next">{{ selectedManager.name }}</span>
            } @else {
              <span class="st-route-stop is-pending">Pick below</span>
            }
          </div>
        </div>
      </div>
    }
  }

  <!-- New manager -->
  <div class="picker-section">
    <div class="picker-section-head">
      <span class="picker-section-title">{{ managerLevelName.one }}</span>
      <span class="picker-section-count" title="Inactive people are not listed">{{ candidates.length }} active</span>
    </div>

    @if (candidates.length > 0) {
      <div class="picker-search">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input
          type="text"
          class="picker-search-input"
          [placeholder]="'Search ' + managerLevelName.many + ' by name or number'"
          [attr.aria-label]="'Search ' + managerLevelName.many"
          [disabled]="isSaving"
          [(ngModel)]="searchTerm" />
      </div>
    }

    <div class="picker-options" role="radiogroup" [attr.aria-label]="managerLevelName.one">
      @for (candidate of filteredCandidates; track candidate.id) {
        <button
          type="button"
          role="radio"
          class="picker-option"
          [class.is-selected]="candidate.id === selectedManagerId"
          [attr.aria-checked]="candidate.id === selectedManagerId"
          [disabled]="isSaving"
          (click)="onPick(candidate.id)">
          <span class="st-avatar is-small" [ngClass]="levelClassOf(candidate)" aria-hidden="true">
            {{ initials(candidate) }}
          </span>
          <span class="picker-option-main">
            <span class="picker-option-name">{{ candidate.name }}</span>
            <span class="picker-option-meta">
              #{{ candidate.employee_number }}, {{ candidate.reports.length }}
              {{ candidate.reports.length === 1 ? 'report' : 'reports' }}
            </span>
          </span>
          @if (candidate.id === currentManagerId) {
            <span class="picker-option-tag">Current</span>
          }
          <span class="picker-option-check" aria-hidden="true"><i class="bi bi-check-lg"></i></span>
        </button>
      } @empty {
        <div class="picker-empty">
          @if (candidates.length === 0) {
            There are no active {{ managerLevelName.many }} to choose from.
          } @else {
            No {{ managerLevelName.many }} match "{{ searchTerm.trim() }}".
          }
        </div>
      }
    </div>

    @for (message of managerErrors; track $index) {
      <div class="picker-field-error" role="alert">
        <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
        {{ message }}
      </div>
    }
  </div>
</div>

<div class="st-dialog-footer">
  <button type="button" class="btn btn-light ms-auto" [disabled]="isSaving" (click)="activeModal.dismiss()">
    Cancel
  </button>
  <button
    type="button"
    class="btn btn-primary picker-save d-flex align-items-center gap-2"
    [disabled]="!canSave"
    (click)="onSave()">
    @if (isSaving) {
      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      Saving...
    } @else {
      {{ saveLabel }}
    }
  </button>
</div>
